import { useQuery } from '@tanstack/react-query';
import api from '../../http';

export type ManagementPlanStatus = 'active' | 'inactive' | 'deleted';
export type ManagementPlanInterval = 'day' | 'week' | 'month' | 'year';

export interface ManagementPlanDTO {
	_id: string;
	itemId: string;
	name: string;
	description?: string;
	hierarchy: string | { _id: string; name: string };
	status: ManagementPlanStatus;
	paymentMethods: string[];
	price: number;
	recurrentPayment: boolean;
	isListable: boolean;
	interval: ManagementPlanInterval;
	intervalCount: number;
	gracePeriod?: number | null;
	notifyUnpayment: boolean;
	notifyInvoice: boolean;
	maxInstallments: number;
	maxProps: number;
	maxSamples: number;
	advantages: string[];
	linkedUsers?: number;
}

export interface PaginatedManagementPlansResponse {
	docs: ManagementPlanDTO[];
	totalDocs: number;
	limit: number;
	totalPages: number;
	page: number;
}

export interface UseGetManagementPlansParams {
	page?: number;
	limit?: number;
	enabled?: boolean;
}

export function useGetManagementPlans({
	page = 1,
	limit = 1000,
	enabled = true,
}: UseGetManagementPlansParams = {}) {
	const query = useQuery<PaginatedManagementPlansResponse>({
		queryKey: ['management-plans', page, limit],
		enabled,
		queryFn: async () => {
			const { data } = await api.get<PaginatedManagementPlansResponse>('/management/plan/findAll', {
				params: { page, limit },
			});
			return data;
		},
	});

	return {
		...query,
		data: query.data?.docs ?? [],
		pagination: query.data
			? { totalDocs: query.data.totalDocs, totalPages: query.data.totalPages, page: query.data.page }
			: null,
	};
}
